import { computed, inject, Injectable, signal } from '@angular/core';
import type { PronunciationSpeech } from '../../models/quiz';
import { ExclusiveAudioPlaybackService } from '../exclusive-audio-playback/exclusive-audio-playback';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';

export interface PronunciationPlayback {
  owner: object;
  id: string;
  status: 'loading' | 'playing';
}

export interface PronunciationPlayRequest {
  text: string;
  questionId: string;
  /** Lets the caller stop only its own audio. */
  owner: object;
  /** Tells the caller which of its elements is loading or playing. */
  id: string;
  /** How fast the speech plays, the normal speed when omitted. */
  rate?: number;
}

/** The speed of the slow sentence, slow enough to follow each sound without distorting the voice. */
export const SLOW_PLAYBACK_RATE = 0.7;

interface SpeechRequest {
  speech: Promise<PronunciationSpeech>;
  expiresAtMs?: number;
}

/**
 * Reads aloud the sentence of a voice question, or one of its words, with the Azure voice of the
 * book language. The reader server relays Exercises, so the Azure key never reaches the browser.
 */
@Injectable({ providedIn: 'root' })
export class PronunciationSpeechService {
  private static readonly EXPIRY_MARGIN_MS = 60_000;
  private readonly readerApi = inject(ReaderApi);
  private readonly metadataService = inject(MetadataService);
  private readonly exclusiveAudioPlayback = inject(ExclusiveAudioPlaybackService);
  private readonly speechRequests = new Map<string, SpeechRequest>();
  private readonly relId = computed(
    () => this.metadataService.metadata()?.userConfig?.bookId ?? '',
  );
  private readonly language = computed(() => this.metadataService.metadata()?.metadata.language);
  private audio?: HTMLAudioElement;

  readonly playback = signal<PronunciationPlayback | undefined>(undefined);

  /** A book in a language Azure does not support shows no speaker. */
  readonly isSupported = this.metadataService.isPronunciationLanguage;

  /** Requested once per question and text while its signed URL is valid. */
  async load(text: string, questionId: string): Promise<PronunciationSpeech> {
    const requestKey = this.toRequestKey(text, questionId);
    const cachedRequest = this.speechRequests.get(requestKey);
    if (cachedRequest && !PronunciationSpeechService.isExpired(cachedRequest)) {
      return cachedRequest.speech;
    }

    const speechRequest: SpeechRequest = {
      speech: this.readerApi.getPronunciationSpeech({
        relId: this.relId(),
        questionId,
        text,
        language: this.language(),
      }),
    };
    speechRequest.speech.then(
      ({ expiresAt }) => (speechRequest.expiresAtMs = new Date(expiresAt).getTime() || 0),
      () => this.speechRequests.delete(requestKey),
    );
    this.speechRequests.set(requestKey, speechRequest);
    return speechRequest.speech;
  }

  /** Resolves once playback starts, or silently when it could not. */
  async play({ text, questionId, owner, id, rate = 1 }: PronunciationPlayRequest): Promise<void> {
    const audio = this.getAudio();
    audio.pause();
    this.playback.set({ owner, id, status: 'loading' });

    try {
      const { url } = await this.load(text, questionId);
      if (!this.isCurrent(owner, id)) return;
      audio.src = url;
      // Setting the source resets the rate, so it is applied on every play
      audio.playbackRate = rate;
      this.exclusiveAudioPlayback.claim(audio);
      await audio.play();
      if (this.isCurrent(owner, id)) this.playback.set({ owner, id, status: 'playing' });
    } catch {
      this.speechRequests.delete(this.toRequestKey(text, questionId));
      if (this.isCurrent(owner, id)) this.playback.set(undefined);
    }
  }

  /** Only stops the audio `owner` started; stops any audio when omitted. */
  stop(owner?: object): void {
    const currentPlayback = this.playback();
    if (!currentPlayback || (owner && currentPlayback.owner !== owner)) return;
    this.audio?.pause();
    this.playback.set(undefined);
  }

  private toRequestKey(text: string, questionId: string): string {
    return `${this.relId()}|${questionId}|${this.language() ?? ''}|${text}`;
  }

  private isCurrent(owner: object, id: string): boolean {
    const currentPlayback = this.playback();
    return currentPlayback?.owner === owner && currentPlayback.id === id;
  }

  private getAudio(): HTMLAudioElement {
    if (this.audio) return this.audio;
    const audio = new Audio();
    audio.addEventListener('ended', () => {
      this.playback.set(undefined);
    });
    audio.addEventListener('pause', () => {
      if (this.playback()?.status === 'playing') this.playback.set(undefined);
    });
    this.audio = audio;
    return audio;
  }

  private static isExpired({ expiresAtMs }: SpeechRequest): boolean {
    return (
      expiresAtMs !== undefined &&
      expiresAtMs - PronunciationSpeechService.EXPIRY_MARGIN_MS <= Date.now()
    );
  }
}
